import React, { ChangeEvent, useState } from 'react';
import EmailInputElement from '../../../components/input/v3/EmailInputElement';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { SortingItem } from '../../../utils/api-objects/SortingItem';
import { EMAIL_REGEX } from '../../../utils/Constants';
import { handleShopifyProductName, validateEmail } from '../../../utils/Functions';
import { useUserStore } from '../../../utils/stores/useUserStore';

interface SortingItemCardProps {
    item: SortingItem;
    index: number;
    isToggled: boolean;
    onToggle: (index: number, isToggled: boolean) => void;
    giftEmail: string;
    onGiftEmailChange: (index: number, email: string) => void;
    onGiftSaved: (index: number) => void;
    dnaTestSelected: boolean;
    subSelected: boolean;
    disabled?: boolean;
}

export const SortingItemCard: React.FC<SortingItemCardProps> = ({
    item,
    index,
    isToggled,
    onToggle,
    giftEmail,
    onGiftEmailChange,
    onGiftSaved,
    dnaTestSelected,
    subSelected,
    disabled = false
}) => {
    const { userData } = useUserStore();
    const currentUserEmail = userData.email;
    const [showGiftInput, setShowGiftInput] = useState(false);
    const [emailError, setEmailError] = useState('');

    // Determine the item state
    const isBoughtByMe = item.buyer === currentUserEmail;
    const isGiftedToMe = item.redeemable_by === currentUserEmail;
    const giftedBy = item.redeemable_by && item.redeemable_by !== currentUserEmail ? item.redeemable_by : null;
    const giftedTo = giftEmail && giftEmail !== currentUserEmail ? giftEmail : null;
    
    // Determine if gift box should be shown
    const shouldShowGiftBox = (giftEmail && !showGiftInput) && (
        // Case 1: No redeemable_by and I added one
        (!item.redeemable_by && giftEmail) ||
        // Case 2: There is a redeemable_by but I changed it
        (item.redeemable_by && giftEmail && giftEmail !== item.redeemable_by)
    );

    // Determine if there's a meaningful change to confirm
    const hasMeaningfulChange = () => {
        // If you're the buyer and there's already a redeemable_by
        if (isBoughtByMe && item.redeemable_by) {
            // Only meaningful if you changed the email
            return giftEmail && giftEmail !== item.redeemable_by;
        }
        // For other cases, any gift email is meaningful
        return giftEmail && giftEmail.trim() !== '';
    };

    const handleGiftClick = () => {
        setShowGiftInput(true);
        // If there's already a redeemable_by, show it as current recipient
        if (item.redeemable_by && !giftEmail) {
            onGiftEmailChange(index, item.redeemable_by);
        }
        setEmailError('');
    };

    const handleGiftEmailSubmit = () => {
        const validationError = validateEmail(giftEmail);
        if (validationError) {
            setEmailError(validationError);
            return;
        }
        
        // Prevent saving the same email that's already set
        if (item.redeemable_by && giftEmail === item.redeemable_by) {
            setEmailError('Questo prodotto è già regalato a questa persona');
            return;
        }
        
        if (giftEmail.trim()) {
            setShowGiftInput(false);
            setEmailError('');
            // Mark the gift as saved
            onGiftSaved(index);
        }
    };

    const handleCancelGift = () => {
        setShowGiftInput(false);
        onGiftEmailChange(index, '');
        setEmailError('');
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        const email = e.target.value;
        onGiftEmailChange(index, email);
        
        // Clear error when user starts typing
        if (emailError) {
            setEmailError('');
        }
        
        // Check if email is the same as current user's email
        if (email && currentUserEmail && email.toLowerCase() === currentUserEmail.toLowerCase()) {
            setEmailError('Non puoi regalare un prodotto a te stesso');
            return;
        }
        
        // Validate email format in real-time
        if (email && !EMAIL_REGEX.test(email)) {
            setEmailError('Inserisci un indirizzo mail valido');
        } else {
            setEmailError('');
        }
    };

    const isEmailValid = EMAIL_REGEX.test(giftEmail) && 
        (!currentUserEmail || giftEmail.toLowerCase() !== currentUserEmail.toLowerCase()) &&
        (!item.redeemable_by || giftEmail !== item.redeemable_by);

    return (
        <div className="flex flex-col w-full bg-white rounded-md p-4 shadow-sm border border-solid border-border">
            <div className="w-full h-full space-y-sm">
                {/* Header with product name and gift status */}
                <div className='flex items-center justify-between w-full'>
                    <div className="flex flex-col">
                        <div className="text-xs font-semibold text-gray-900 md:text-sm">
                            {handleShopifyProductName(item.product_type)}
                        </div>
                        {isBoughtByMe && item.redeemable_by && (
                            <div className="text-xs text-green-600 font-medium mt-1">
                                🎁 Hai regalato questo prodotto a {item.redeemable_by}
                            </div>
                        )}
                        {isGiftedToMe && item.buyer && (
                            <div className="text-xs text-blue-600 font-medium mt-1">
                                🎁 Questo prodotto ti è stato regalato da {item.buyer}
                            </div>
                        )}
                    </div>
                    <div className="ml-4 flex space-x-2 items-center">
                        <div className='text-xxs font-semibold text-primary'>Assegna a me</div>
                        <Switch
                            checked={isToggled}
                            onCheckedChange={(checked) => onToggle(index, checked)}
                            disabled={disabled}
                            className={`${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                    </div>
                </div>

                {/* Gift section */}
                <div className="space-y-2">
                    {showGiftInput ? (
                        <div className="space-y-2">
                            {item.redeemable_by && (
                                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    <div className="font-medium">Destinatario attuale:</div>
                                    <div>{item.redeemable_by}</div>
                                </div>
                            )}
                            <EmailInputElement
                                name={`gift-email-${index}`}
                                label=""
                                inputValue={giftEmail}
                                onChange={handleEmailChange}
                                placeholder="Inserisci l'email del nuovo destinatario"
                                disabled={false}
                                marginBottom={0}
                                marginTop={0}
                                fontSize={14}
                            />
                            {emailError && (
                                <div className="text-xs text-red-500 mt-1">
                                    {emailError}
                                </div>
                            )}
                            <div className="flex space-x-2">
                                <Button 
                                    onClick={handleGiftEmailSubmit}
                                    disabled={!giftEmail.trim() || !isEmailValid}
                                    size="sm"
                                    className="flex-1"
                                >
                                    Regala
                                </Button>
                                <Button 
                                    onClick={handleCancelGift}
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                >
                                    Annulla
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex space-x-2">
                            <Button 
                                onClick={handleGiftClick}
                                variant="outline"
                                size="sm"
                                className="flex-1"
                            >
                                {isBoughtByMe && item.redeemable_by 
                                    ? "🔄 Regala a qualcun altro"
                                    : isGiftedToMe
                                    ? "🎁 Regala a qualcun altro"
                                    : "🎁 Regala a qualcuno"
                                }
                            </Button>
                        </div>
                    )}
                </div>

                {/* Show current gift email if set */}
                {shouldShowGiftBox && (
                    <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-200">
                        <div className="font-medium text-blue-800">🎁 Pronto per regalare a:</div>
                        <div className="text-blue-700">{giftEmail}</div>
                        <div className="text-xs text-blue-600 mt-1">
                            Clicca "Conferma" per completare il regalo
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}; 